/**
 * SSE client for `GET /v1/events` (04-architecture § 8.6).
 *
 * fetch-based on purpose: the token goes into the `Authorization` header, never
 * into the query string (server 06-auth § 3). No `EventSource` polyfill.
 */

export type SseState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type SseEventName =
  | 'occupancy'
  | 'desk'
  | 'booking'
  | 'door'
  | 'notification'
  | 'pass_refresh'
  | 'account'
  | 'terminal_status'
  | 'staff_task'
  | 'enquiry'
  | 'terminal_ack'
  | 'terminal_log';

export interface SseMessage {
  event: SseEventName | string;
  data: unknown;
  id?: string;
}

export interface SseClientOptions {
  url: string;
  getToken: () => string | null;
  fetch?: typeof globalThis.fetch;
  onEvent: (message: SseMessage) => void;
  onStateChange?: (state: SseState) => void;
  /** Called on 401 so the app can run its logout flow. */
  onUnauthorized?: () => void;
}

/** The server sends a comment every 25 s; nothing for 60 s means the link is dead. */
export const HEARTBEAT_TIMEOUT_MS = 60_000;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16_000, 30_000];
/** Backoff resets after this much stable connection time. */
const STABLE_MS = 60_000;

export interface SseClient {
  start(): void;
  stop(): void;
  readonly state: SseState;
}

export function createSseClient(options: SseClientOptions): SseClient {
  const doFetch = options.fetch ?? globalThis.fetch;
  let controller: AbortController | null = null;
  let stopped = true;
  let attempt = 0;
  let state: SseState = 'closed';
  let watchdog: ReturnType<typeof setTimeout> | null = null;

  const setState = (next: SseState): void => {
    state = next;
    options.onStateChange?.(next);
  };

  const armWatchdog = (): void => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => controller?.abort(), HEARTBEAT_TIMEOUT_MS);
  };

  const scheduleReconnect = (): void => {
    if (stopped) return;
    setState('reconnecting');
    const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] as number;
    attempt += 1;
    // Full jitter, so a server restart does not produce a thundering herd.
    setTimeout(() => void connect(), Math.random() * delay);
  };

  async function connect(): Promise<void> {
    if (stopped) return;
    const token = options.getToken();
    if (!token) return scheduleReconnect();

    controller = new AbortController();
    setState('connecting');
    const openedAt = Date.now();

    try {
      const response = await doFetch(options.url, {
        headers: { Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      if (response.status === 401) {
        stopped = true;
        setState('closed');
        options.onUnauthorized?.();
        return;
      }
      if (!response.ok || !response.body) return scheduleReconnect();

      setState('open');
      armWatchdog();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        armWatchdog();
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const message = parseFrame(frame);
          if (message) options.onEvent(message);
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch {
      /* aborted or network error — handled by the reconnect below */
    } finally {
      if (watchdog) clearTimeout(watchdog);
      watchdog = null;
      if (Date.now() - openedAt > STABLE_MS) attempt = 0;
      if (!stopped) scheduleReconnect();
    }
  }

  return {
    get state() {
      return state;
    },
    start() {
      if (!stopped) return;
      stopped = false;
      attempt = 0;
      void connect();
    },
    stop() {
      stopped = true;
      controller?.abort();
      controller = null;
      if (watchdog) clearTimeout(watchdog);
      watchdog = null;
      setState('closed');
    },
  };
}

/** Parses one SSE frame; comment-only frames (the heartbeat) return `null`. */
export function parseFrame(frame: string): SseMessage | null {
  let event: string | null = null;
  let id: string | undefined;
  const data: string[] = [];

  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // heartbeat
    const separator = line.indexOf(':');
    const field = separator === -1 ? line : line.slice(0, separator);
    const value = separator === -1 ? '' : line.slice(separator + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
    else if (field === 'id') id = value;
  }

  if (!event || data.length === 0) return null;
  const raw = data.join('\n');
  try {
    return { event, data: JSON.parse(raw), ...(id ? { id } : {}) };
  } catch {
    return { event, data: raw, ...(id ? { id } : {}) };
  }
}

/**
 * § 8.6 — event → cache invalidation. Returns the query-key prefixes to
 * invalidate; the app applies them to its `QueryClient`.
 */
export function invalidationFor(event: SseEventName | string, data: unknown): string[][] {
  const locationId =
    typeof data === 'object' && data !== null ? (data as { locationId?: string }).locationId : undefined;
  switch (event) {
    case 'occupancy':
      return locationId ? [['locations', locationId, 'occupancy']] : [];
    case 'desk':
      return locationId
        ? [
            ['locations', locationId, 'availability'],
            ['locations', locationId, 'occupancy'],
          ]
        : [];
    case 'booking':
      return [['bookings'], ['me'], ['orders']];
    case 'notification':
      return [['notifications']];
    case 'account':
      return [['me'], ['me', 'contracts'], ['me', 'payment-method']];
    // The three admin events each also move a badge in the backoffice shell
    // (10-web-admin § 3), which reads its counts from `/admin/nav`.
    case 'terminal_status':
      return [
        ['admin', 'terminals'],
        ['admin', 'nav'],
      ];
    case 'staff_task':
      return [
        ['admin', 'staff-tasks'],
        ['admin', 'nav'],
      ];
    case 'enquiry':
      return [
        ['admin', 'enquiries'],
        ['admin', 'nav'],
      ];
    // `door`, `pass_refresh`, `terminal_ack` and `terminal_log` are consumed by their feature stores.
    default:
      return [];
  }
}

/** § 8.6 rule 4 — the server does not replay, so a reconnect refetches these. */
export const RECONNECT_INVALIDATIONS: string[][] = [['me'], ['bookings'], ['locations'], ['notifications']];
